import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  ManageAccounts as ManageAccountsIcon,
  MonetizationOn as MonetizationOnIcon,
  Notifications as NotificationsIcon,
  Password as PasswordIcon,
  PeopleAlt as PeopleAltIcon,
  Person as PersonIcon,
  Phonelink as PhonelinkIcon,
  Settings as SettingsIcon,
  Wysiwyg as WysiwygIcon,
} from '@mui/icons-material';

import { appRoutes } from '@clearverify/constants';

import {
  BaseLayoutLeftMenu,
  FullscreenLoader,
  LeftMenuLogo,
  MenuItem,
  ResourcesListItem,
} from 'src/components/common';
import { useCurrentUserEmployer } from 'src/hooks/employer';
import { useGlobalState } from 'src/hooks/shared';
import { GetInTouchModal } from 'src/modals/getInTouch';
import { employerReceivesOwnRevenueShare } from 'src/utils/revenueShare';

export const LayoutLeftMenu: React.FC = () => {
  const { leftMenuDrawerState } = useGlobalState();

  const { employer, loading } = useCurrentUserEmployer();

  const myAccountItemsList: MenuItem[] = React.useMemo(
    () => [
      {
        link: appRoutes.account.personalInformation,
        title: 'Personal Information',
        icon: PersonIcon,
      },
      {
        link: appRoutes.account.companyInformation,
        title: 'Company information',
        icon: AccountBalanceIcon,
      },
      {
        link: appRoutes.account.employerDisclaimer,
        title: 'Employer Disclaimer',
        icon: InfoIcon,
      },
      {
        link: appRoutes.account.wallet,
        title: 'Wallet',
        icon: MonetizationOnIcon,
        hide: !employer?.revenueShareSetting || !employerReceivesOwnRevenueShare(employer),
      },
      {
        link: appRoutes.account.settings,
        title: 'Settings',
        icon: SettingsIcon,
      },
      {
        link: appRoutes.account.notifications,
        title: 'Notifications',
        icon: NotificationsIcon,
        divider: true,
      },
      {
        link: appRoutes.account.security,
        title: 'Sign-in & Security',
        icon: PasswordIcon,
      },
      {
        link: appRoutes.account.setupMfa,
        title: 'Multi-factor Authentication',
        icon: PhonelinkIcon,
      },
    ],
    [employer],
  );

  if (loading) {
    return (
      <FullscreenLoader
        sx={{
          height: '500px',
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <BaseLayoutLeftMenu
        leftMenuDrawerState={leftMenuDrawerState}
        menuItemsList={menuItems}
        myAccountItemsList={myAccountItemsList}
        footer={<ResourcesListItem />}
        rootRoute={appRoutes.inbox.index}
        logoutRoute={appRoutes.auth.logout}
        leftMenuLogo={<LeftMenuLogo showIcon={!leftMenuDrawerState.isOpened} />}
      />
      <GetInTouchModal />
    </React.Fragment>
  );
};

const menuItems: MenuItem[] = [
  {
    link: appRoutes.inbox.index,
    title: 'Home',
    icon: HomeIcon,
  },
  {
    link: appRoutes.employees.index,
    title: 'Employees',
    icon: PeopleAltIcon,
  },
  {
    link: appRoutes.employerStaff.index,
    title: 'Staff',
    icon: ManageAccountsIcon,
  },
  {
    link: appRoutes.reports.index,
    title: 'Dashboard & Reports',
    icon: WysiwygIcon,
  },
];
